import React, { useState, useEffect } from 'react'
import { B1BatteryAutonomy, E3SgReady, J1RightarrowD } from '@e3dc-react/icons'
import { Box, Card, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import SettingsPage from './SettingsPage'
import GuiPieChart from 'src/Elements/LiveView/AlternativeView/GuiPieChart'
import { BatteryBadge, SgReadyBadge, TipsCarousel } from '../components'
import { AI360_DETAILS_PAGE_ROUTE } from '../consts'
import { useDynamicEms, useDynamicEmsData } from 'src/Libs/dynamic-power-prices'
import { formatISO, startOfHour, endOfHour } from '@e3dc-react/shell/Libs/date'
import { useFeedInPowerData } from 'src/Libs/installation-settings/hooks'
import { useFetchCurrentPowerSellPrice, useFetchTariffsMode } from '../hooks'
import { useInstallationExtensions } from 'src/Libs/installation-extensions'
import DetailsPageContainer from './DetailsPageContainer'
import aiAnimation from 'src/Libs/ai-360/assets/ai360-animation.json'
import Lottie from 'lottie-react'
import { useFetchDynamicPowerPrice } from 'src/Libs/dynamic-power-prices/hooks'

const useStyles = makeStyles()(theme => ({
  stackWrapper: {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 4, 0),
  },
  inactiveStack: {
    borderBottom: 'unset',
  },
  navigationButton: {
    minHeight: theme.spacing(4),
    margin: theme.spacing(2, 2, 0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  inactiveText: {
    color: theme.palette.grey.grey05,
  },
  guiPieChartCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  blackGuiPieChartCard: {
    backgroundColor: theme.palette.common.black,
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 32,
  },
  lottieAnimation: {
    position: 'absolute',
    width: 930,
    height: '100%',
    pointerEvents: 'none',
  },
  lottieAnimationMobile: {
    top: 0,
    [theme.breakpoints.down('sm')]: {
      width: 880,
    },
  },
}))

const HomePage: React.FC = () => {
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const location = useLocation()
  const { t } = useCustomTranslation()
  const { classes, cx } = useStyles()
  const { data: dynamicEmsSettings, refetch: refetchDynamicEmsSettings } = useDynamicEms(installationId, !!installationId)
  const { data: dynamicEmsData } = useDynamicEmsData(installationId, formatISO(startOfHour(new Date())), formatISO(endOfHour(new Date())))
  const { feedInPowerData } = useFeedInPowerData(storageId)
  const { powerSellPrice, refetch: refetchPowerSellPrice } = useFetchCurrentPowerSellPrice(+installationId!)
  const { extensions } = useInstallationExtensions()
  const { tariffsMode } = useFetchTariffsMode(+installationId!)
  const { refetch: refetchDynamicPowerPrices } = useFetchDynamicPowerPrice(storageId)

  const [isOpenSettingsDialog, setIsOpenSettingsDialog] = useState(false)
  const [isAI360Active, setIsAI360Active] = useState(false)
  const isSgReadyEnabled = dynamicEmsSettings?.isSgReadyEnabled
  const navigate = useNavigate()
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))

  useEffect(() => {
    if (location.state?.shouldDisplaySettings) {
      setIsOpenSettingsDialog(true)
    }
  }, [location.state?.shouldDisplaySettings])

  useEffect(() => {
    if (dynamicEmsSettings) {
      setIsAI360Active(dynamicEmsSettings.isActive)
    } else {
      // "Dynamic EMS not activated" state is treated as inactive AI360, so we set it to false to show the correct UI. Once the user activates Dynamic EMS, the correct state will be reflected in the UI.
      setIsAI360Active(false)
    }
  }, [dynamicEmsSettings])

  const handleNavigateToDetails = (): void => {
    navigate(`${AI360_DETAILS_PAGE_ROUTE}`)
  }

  const handleOpenSettingsDialog = (): void => {
    setIsOpenSettingsDialog(true)
  }

  const handleCloseSettingsDialog = (): void => {
    setIsOpenSettingsDialog(false)
  }

  const handleRefetch = (): void => {
    refetchDynamicEmsSettings()
    refetchPowerSellPrice()
    refetchDynamicPowerPrices()
  }

  const settingsPage = (
    <SettingsPage
      isOpen={isOpenSettingsDialog}
      onClose={handleCloseSettingsDialog}
      dynamicEmsSettings={dynamicEmsSettings}
      feedInPowerData={feedInPowerData}
      powerSellPrice={powerSellPrice}
      tariffsMode={tariffsMode}
      extensions={extensions}
      refetch={handleRefetch}
    />
  )

  const batteryStatusSection = (
    <Box className={classes.statusRow} sx={{ marginBottom: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <B1BatteryAutonomy className={cx({ [classes.inactiveText]: !isAI360Active })} />
        <Typography variant="subtitle2" className={cx({ [classes.inactiveText]: !isAI360Active })}>
          {t('ai360.main-page.battery-status')}
        </Typography>
      </Box>
      {dynamicEmsData?.length && isAI360Active ? <BatteryBadge status={dynamicEmsData.at(-1)!.emsBatteryMode} /> : null}
    </Box>
  )

  const sgReadyStatusSection = isSgReadyEnabled ? (
    <Box className={classes.statusRow} sx={{ pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <E3SgReady className={cx({ [classes.inactiveText]: !isAI360Active })} />
        <Typography variant="subtitle2" className={cx({ [classes.inactiveText]: !isAI360Active })}>
          {t('ai360.main-page.sg-ready-status')}
        </Typography>
      </Box>
      {dynamicEmsData?.length && isAI360Active && dynamicEmsData.at(-1)?.emsSgReadyMode ? (
        <SgReadyBadge status={dynamicEmsData.at(-1)!.emsSgReadyMode!} />
      ) : null}
    </Box>
  ) : null

  /** Desktop View: The details chart  is directly shown in Home page. Don't show Carousel when AI360 is inactive */
  if (upMd) {
    return (
      <Stack gap={2} height="100%">
        <Stack flexDirection="row" alignItems="stretch" justifyContent="center" gap={2}>
          {/* GUI Pie Chart */}
          <Card className={cx(classes.guiPieChartCard, { [classes.blackGuiPieChartCard]: isAI360Active })} sx={{ paddingTop: 2, maxWidth: '900px' }}>
            {isAI360Active && (
              <Lottie animationData={aiAnimation} className={classes.lottieAnimation} rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }} />
            )}
            <GuiPieChart
              storageId={storageId}
              isDynamicPowerPriceEnabled={true}
              isInactive={!isAI360Active}
              sx={{ paddingTop: '4px', paddingRight: '2px' }}
              textColor={theme.palette.common.white}
              showAI360V2Animation={isAI360Active}
            />
            {isAI360Active && (
              <Box flex={1} display="flex" justifyContent="end" width="100%" mt={2} zIndex={2}>
                <Typography variant="subtitle2" color={theme.palette.common.white} bgcolor={theme.palette.common.black} borderRadius={2}>
                  {t('ai360.main-page.last-hour-hint')}
                </Typography>
              </Box>
            )}
          </Card>

          <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 0 }}>
            <Stack sx={{ pt: 2, px: 4 }}>
              {batteryStatusSection}
              {sgReadyStatusSection}
            </Stack>

            {/* Settings Page Navigation  */}
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 4, py: 2 }}
              borderTop={`1px solid ${theme.palette.divider}`}
              onClick={handleOpenSettingsDialog}
            >
              <Stack>
                <Typography variant="h3">{t('ai360.main-page.settings')}</Typography>
                {!isAI360Active && <Typography variant="body2">{t('ai360.main-page.settings-to-activate')}</Typography>}
              </Stack>
              <IconButton>
                <J1RightarrowD sx={{ color: 'grey.grey00' }} />
              </IconButton>
            </Stack>

            {/* Settings page Dialog */}
            {isOpenSettingsDialog && settingsPage}
          </Card>
        </Stack>
        <DetailsPageContainer />
      </Stack>
    )
  }

  /* Mobile View: Details chart is not shown in Home Page. Shows Carousel when AI360 is inactive */
  return (
    <Stack gap={2}>
      <Card sx={{ px: 0 }}>
        <Box className={cx(classes.guiPieChartCard, { [classes.blackGuiPieChartCard]: isAI360Active })} px={2} pt={3} pb={2}>
          {isAI360Active && (
            <Lottie
              animationData={aiAnimation}
              className={cx(classes.lottieAnimation, classes.lottieAnimationMobile)}
              rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
            />
          )}
          <GuiPieChart
            storageId={storageId}
            isDynamicPowerPriceEnabled={true}
            isInactive={!isAI360Active}
            sx={{ paddingBottom: '1px', paddingRight: '2px' }}
            textColor={theme.palette.common.white}
            showAI360V2Animation={isAI360Active}
          />
          {isAI360Active && (
            <Box flex={1} display="flex" justifyContent="center" width="100%" mt={2} zIndex={2}>
              <Typography variant="subtitle2" color={theme.palette.common.white} bgcolor={theme.palette.common.black} borderRadius={2}>
                {t('ai360.main-page.last-hour-hint')}
              </Typography>
            </Box>
          )}
        </Box>
        <Stack className={cx(classes.stackWrapper, { [classes.inactiveStack]: !isAI360Active })} sx={{ mx: -2 }}>
          {batteryStatusSection}
          {sgReadyStatusSection}
        </Stack>
        {isAI360Active && (
          <Box className={classes.navigationButton} onClick={handleNavigateToDetails}>
            <Typography variant="h3">{t('ai360.main-page.details')}</Typography>
            <IconButton>
              <J1RightarrowD sx={{ color: 'grey.grey00' }} />
            </IconButton>
          </Box>
        )}
      </Card>

      {/* Carousel */}
      {!isAI360Active && <TipsCarousel />}

      {/* Settings Page Navigation  */}
      <Card sx={{ mt: isAI360Active ? 0 : -2, px: 0 }}>
        <Box className={classes.navigationButton} onClick={handleOpenSettingsDialog}>
          <Stack>
            <Typography variant="h3">{t('ai360.main-page.settings')}</Typography>
            {!isAI360Active && <Typography variant="body2">{t('ai360.main-page.settings-to-activate')}</Typography>}
          </Stack>
          <IconButton>
            <J1RightarrowD sx={{ color: 'grey.grey00' }} />
          </IconButton>
        </Box>
      </Card>

      {/* Settings page Dialog */}
      {isOpenSettingsDialog && settingsPage}
    </Stack>
  )
}

export default HomePage
